import { useEffect, useRef, useState } from "react";
import AppBar from "@/components/AppBar";
import { emailError } from "@/lib/validate";
import { withDefaults } from "@/lib/pricing";
import { motion, AnimatePresence } from "framer-motion";
import { api, asArray, mediaUrl, formatApiErrorDetail } from "@/lib/api";
import { Link } from "react-router-dom";
import { toast } from "@/lib/toast";
import MasterSearch from "@/components/MasterSearch";
import CitySelect from "@/components/CitySelect";
import VenueCard, { VenueCardSkeleton, SmartImg } from "@/components/VenueCard";
import FlipCounter from "@/components/FlipCounter";
import { Divider } from "@/components/Ornaments";
import { useCity } from "@/context/CityContext";
import { useLive, useTick } from "@/context/LiveContext";
import { CATEGORIES, catLabel } from "@/lib/categories";
import { Star, ArrowRight, Quote, ChevronLeft, ChevronRight, Phone, CalendarCheck, Users, Plus, Minus } from "lucide-react";

const HERO = "https://images.unsplash.com/photo-1761472606347-bfebc5a3e546?crop=entropy&cs=srgb&fm=jpg&w=1800";

/* The bullseye from the centre of the logo, with a dart in it */
function Bullseye({ className = "" }) {
  return (
    <svg viewBox="-200 -200 400 400" className={className} aria-hidden>
      {[190, 158, 126, 94, 62, 30].map((r, i) => (
        <circle key={r} r={r} fill="none" stroke={i % 2 ? "rgba(252,251,212,.6)" : "rgba(251,247,244,.35)"} strokeWidth={i === 5 ? 30 : 1.4}/>
      ))}
      <circle r="12" fill="#FCFBD4"/>
      <g transform="rotate(-24) translate(0,0)">
        <polygon points="0,0 22,-4 22,4" fill="#FBF7F4"/>
        <rect x="22" y="-5" width="54" height="10" rx="4" fill="#FCFBD4"/>
        <rect x="76" y="-2" width="56" height="4" fill="#FBF7F4"/>
        <polygon points="124,0 176,-24 164,0" fill="#FCFBD4"/>
        <polygon points="124,0 176,24 164,0" fill="#FBF7F4" opacity=".8"/>
      </g>
    </svg>
  );
}

export default function Home() {
  const { city, setCity } = useCity();
  const tick = useTick();
  const activeCity = city || "Belgaum";
  const [featured, setFeatured] = useState([]);
  const [premium, setPremium] = useState([]);
  const [loading, setLoading] = useState(true);
  const [testimonials, setTestimonials] = useState([]);
  const [popular, setPopular] = useState([]);
  const [blogs, setBlogs] = useState([]);

  useEffect(() => {
    api.get("/testimonials").then((r) => setTestimonials(asArray(r.data))).catch(() => {});
    api.get("/config/popular-cities").then((r) => setPopular(asArray(r.data))).catch(() => {});
    api.get("/blogs", { params: { limit: 3 } }).then((r) => setBlogs(asArray(r.data?.items))).catch(() => {});
  }, [tick]);

  useEffect(() => {
    if (!tick) setLoading(true);
    api.get(`/venues/highlighted/${encodeURIComponent(activeCity)}`).then((r) => {
      const list = asArray(r.data); setFeatured(list.slice(0, 6)); setPremium(list.slice(0, 10));
    }).catch(() => {}).finally(() => setLoading(false));
  }, [activeCity, tick]);

  return (
    <div>
      <AppBar/>
      {/* HERO */}
      <section className="relative min-h-[88vh] flex items-end overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0">
          <img src={HERO} className="w-full h-full object-cover" alt="" fetchpriority="high"/>
          <div className="absolute inset-0 hero-overlay"/>
          <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(42,15,17,.72)_0%,rgba(42,15,17,.25)_60%,transparent_100%)]"/>
        </div>
        <motion.div initial={{ opacity: 0, scale: 0.8, rotate: -20 }} animate={{ opacity: 1, scale: 1, rotate: 0 }} transition={{ duration: 1.1, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
          className="hidden lg:block absolute -right-28 top-16 w-[560px] h-[560px] pointer-events-none">
          <Bullseye className="w-full h-full"/>
        </motion.div>

        <div className="relative max-w-[1400px] mx-auto px-4 sm:px-6 md:px-10 pt-14 md:pt-24 pb-12 md:pb-16 w-full text-white z-10">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }} className="max-w-3xl">
            <p className="text-[color:var(--gold-2)] text-lg md:text-xl font-medium mb-4">Weddings, receptions & every function in between</p>
            <h1 className="font-display text-[46px] leading-[1.02] sm:text-[64px] lg:text-[84px]">
              Right venue.<br/>Right people.<br/>Right date.
            </h1>
            <p className="mt-6 text-[17px] md:text-[19px] text-white/85 max-w-2xl leading-relaxed">
              Halls, lawns, caterers, decorators, photographers, anchors and musicians in your city — real photos, free dates on a calendar, and the owner's number on every page.
            </p>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25, duration: 0.9, ease: [0.16, 1, 0.3, 1] }} className="mt-10 text-left">
            <MasterSearch hero/>
          </motion.div>

          <div className="mt-6 flex gap-2 overflow-x-auto no-scrollbar pb-1" data-testid="hero-quick-cats">
            {CATEGORIES.map((c) => (
              <Link key={c.key} to={`/properties?category=${c.key}`} className="shrink-0 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 hover:bg-white/20 border border-white/20 backdrop-blur text-[15px] text-white transition-colors press">
                {c.plural}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 md:px-10 py-20 md:py-24" data-testid="categories-section">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-10">
          <div>
            <Divider label="Everything under one roof"/>
            <h2 className="font-display text-4xl md:text-6xl mt-2 text-[color:var(--maroon)]">Discover by category</h2>
          </div>
          <p className="text-[color:var(--ink-2)] max-w-md md:text-right">The place, the food, the décor, the photos, the host and the music — book them all from one shortlist.</p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
          {CATEGORIES.map((c, i) => (
            <motion.div key={c.key} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: (i % 4) * 0.06 }}>
              <Link to={`/properties?category=${c.key}`} className="group block relative rounded-[22px] overflow-hidden aspect-[4/5] press" data-testid={`cat-${c.key}`}>
                <SmartImg src={null} category={c.key} alt={c.plural} icon={false} className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-[900ms]"/>
                <div className="absolute inset-0 bg-gradient-to-t from-[color:var(--maroon-2)] via-[color:var(--maroon-2)]/25 to-transparent"/>
                <div className="absolute inset-x-0 bottom-0 p-5 text-white">
                  <div className="font-display text-2xl md:text-[28px] leading-none">{c.plural}</div>
                  <div className="text-[14px] text-white/75 mt-1.5">{c.blurb}</div>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section className="jali py-20 md:py-28 border-y border-[color:var(--line)]" data-testid="how-it-works-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-10 grid lg:grid-cols-[1.1fr_1fr] gap-12 lg:gap-20 items-center">
          <div className="relative">
            <div className="rounded-[22px] overflow-hidden shadow-[0_30px_80px_rgba(77,14,18,0.22)]">
              <img src="https://images.pexels.com/photos/12688995/pexels-photo-12688995.jpeg?auto=compress&cs=tinysrgb&h=900&w=1200" alt="Wedding mandap" className="w-full aspect-[4/5] object-cover"/>
            </div>
          </div>
          <div>
            <h2 className="font-display text-4xl md:text-6xl text-[color:var(--maroon)] leading-[1.02]">Booking for a wedding used to take a month of phone calls.</h2>
            <p className="mt-5 text-[color:var(--ink-2)] leading-relaxed max-w-lg text-[17px]">Here's how families on DartMyVenue do it now — no agents and no commission, just direct conversations with the people who run the place.</p>
            <ol className="mt-10 relative border-l-2 border-dashed border-[color:var(--gold)]/60 ml-5 space-y-9">
              {[
                { t: "Pick your city, date and guest count", d: "Only places and people who are free on your date show up — booked days are marked on every calendar.", I: CalendarCheck },
                { t: "Shortlist, watch the videos, compare prices", d: "Real photos, walkthrough videos, per-plate menus and packages, side by side.", I: Users },
                { t: "Call or WhatsApp the owner directly", d: "Every listing shows the owner's number. You agree the price and pay them — we never take a cut.", I: Phone },
              ].map((s, i) => (
                <li key={i} className="pl-10 relative">
                  <span className="absolute -left-[23px] top-0 w-11 h-11 rounded-full bg-[color:var(--maroon)] text-[color:var(--gold-2)] flex items-center justify-center font-num text-lg">{i + 1}</span>
                  <div className="font-display text-2xl text-[color:var(--maroon)] leading-tight">{s.t}</div>
                  <p className="text-[15px] text-[color:var(--ink-2)] mt-1.5 leading-relaxed">{s.d}</p>
                </li>
              ))}
            </ol>
            <Link to="/properties" className="btn-wine mt-10">Start browsing <ArrowRight className="w-4 h-4"/></Link>
          </div>
        </div>
      </section>

      {/* Six standout */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 md:px-10 py-20 md:py-24" data-testid="featured-section">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-5 mb-10">
          <div>
            <Divider label="Handpicked for you"/>
            <h2 className="font-display text-4xl md:text-6xl mt-2 text-[color:var(--maroon)]">Standout listings in {activeCity}</h2>
          </div>
          <div className="flex items-center gap-3 w-full md:w-auto">
            <div className="w-full md:w-64"><CitySelect value={activeCity} onChange={setCity} testId="home-city-select"/></div>
            <Link to={`/properties?city=${encodeURIComponent(activeCity)}`} className="hidden sm:inline-flex text-[15px] text-[color:var(--maroon)] hover:underline items-center gap-1 font-semibold whitespace-nowrap">All in {activeCity} <ArrowRight className="w-4 h-4"/></Link>
          </div>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {loading && Array.from({ length: 6 }).map((_, i) => <VenueCardSkeleton key={i}/>)}
          {!loading && featured.length === 0 && (
            <div className="col-span-full text-center py-20 text-neutral-500 border border-dashed border-[color:var(--gold)]/50 rounded-3xl">
              No listings in {activeCity} yet. <Link className="text-[color:var(--maroon)] underline" to="/properties">Browse all cities</Link>
            </div>
          )}
          {!loading && featured.map((v) => <VenueCard key={v.id} v={v}/>)}
        </div>
      </section>

      {/* Premium spotlight */}
      <section className="py-16 jali-dark text-[#FBF7F4] overflow-hidden" data-testid="premium-carousel">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-10 flex flex-col sm:flex-row sm:items-end justify-between gap-3 mb-8">
          <div>
            <div className="text-[color:var(--gold-2)] font-semibold">Premium spotlight · {activeCity}</div>
            <h2 className="font-display text-3xl md:text-5xl mt-1">Ten listings families look at first</h2>
          </div>
          <Link to="/pricing" className="text-[15px] text-[color:var(--gold-2)] hover:underline inline-flex items-center gap-1">Claim a premium slot <ArrowRight className="w-4 h-4"/></Link>
        </div>
        {premium.length > 0 ? (
          <div className="overflow-hidden">
            <div className="marquee px-4">
              {[...premium, ...premium].map((v, i) => (
                <Link to={`/property/${v.id}`} key={i} className="min-w-[280px] max-w-[280px] rounded-2xl overflow-hidden block bg-white text-[color:var(--ink)] hover:-translate-y-1 transition-transform" data-testid={`premium-item-${i}`}>
                  <div className="relative"><SmartImg src={v.images?.[0]} category={v.category} className="h-44 w-full object-cover" alt={v.name}/><span className="absolute top-2 left-2 text-[12px] bg-[color:var(--gold-2)] text-[color:var(--maroon-2)] px-2 py-0.5 rounded-full font-bold">#{(i % premium.length) + 1}</span></div>
                  <div className="p-4">
                    <div className="font-display text-xl leading-tight text-[color:var(--maroon)]">{v.name}</div>
                    <div className="text-[13px] text-neutral-600 mt-1">{v.city} · {catLabel(v.category)}</div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        ) : <div className="text-center text-[15px] text-[#FBF7F4]/70 py-10">Premium slots in {activeCity} are open — be the first.</div>}
      </section>

      {/* Popular cities */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 md:px-10 py-20" data-testid="popular-cities-section">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
          <div>
            <Divider label="Where India celebrates"/>
            <h2 className="font-display text-4xl md:text-5xl mt-1 text-[color:var(--maroon)]">Popular wedding cities</h2>
          </div>
          <Link to="/highlighted" className="text-[15px] text-[color:var(--maroon)] hover:underline inline-flex items-center gap-1 font-semibold">See every city <ArrowRight className="w-4 h-4"/></Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
          {popular.map((c, i) => (
            <motion.div key={c.name + i} initial={{ opacity: 0, scale: 0.96 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ delay: (i % 4) * 0.05 }}>
              <Link to={`/properties?city=${encodeURIComponent(c.name)}`} onClick={() => setCity(c.name)} className="group block relative rounded-[22px] overflow-hidden aspect-[4/5] press" data-testid={`city-${c.name}`}>
                <img src={mediaUrl(c.img)} alt={c.name} loading="lazy" className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-700 bg-[color:var(--maroon-2)]"/>
                <div className="absolute inset-0 bg-gradient-to-t from-[color:var(--maroon-2)]/90 via-black/15 to-transparent"/>
                <div className="absolute inset-x-0 bottom-0 p-4 text-white">
                  <div className="font-display text-2xl">{c.name}</div>
                  <div className="text-[14px] text-[color:var(--gold-2)] mt-0.5">{c.tagline || "Explore venues"}</div>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </section>

      <ReelSection/>

      <Testimonials items={testimonials}/>

      <OwnersSection/>

      {blogs.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 md:px-10 pt-20 md:pt-24 pb-20" data-testid="blogs-preview">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
            <div>
              <Divider label="Journal"/>
              <h2 className="font-display text-4xl md:text-5xl mt-1 text-[color:var(--maroon)]">Stories, checklists & ideas</h2>
            </div>
            <Link to="/blogs" className="text-[15px] text-[color:var(--maroon)] hover:underline inline-flex items-center gap-1 font-semibold">All stories <ArrowRight className="w-4 h-4"/></Link>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {blogs.map((p) => (
              <Link to={`/blogs/${p.slug}`} key={p.id} className="card-luxury overflow-hidden group block">
                <div className="aspect-[16/10] overflow-hidden bg-[color:var(--ivory)]"><img src={mediaUrl(p.cover_image)} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" alt={p.title}/></div>
                <div className="p-6">
                  <div className="text-[13px] text-[color:var(--gold)] font-semibold">{p.tags?.[0]}{p.tags?.[0] ? " · " : ""}{p.read_minutes || 4} min read</div>
                  <h3 className="font-display text-2xl leading-tight text-[color:var(--maroon)] mt-2">{p.title}</h3>
                  <p className="text-[15px] text-neutral-600 mt-2 line-clamp-2">{p.excerpt}</p>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      <FAQ/>
      <Newsletter/>
    </div>
  );
}

/* ---------- For venue owners & every wedding business ----------
   Pick your kind of business and see the inquiry that lands on your phone, the plan price for it,
   and what you keep. One interactive moment instead of a feature list. */
const SAMPLE_ASKS = {
  banquet_hall: { who: "Priya Kulkarni", when: "Sat, 12 Dec", msg: "Namaste! Is your hall free on 12 December for our reception? We're expecting about 600 guests. Can we come for a visit this Sunday?" },
  lawn: { who: "Rohit Patil", when: "Sun, 24 Jan", msg: "Hi, looking for an open lawn for a wedding on 24 January, around 900 guests, evening muhurat. Do you allow outside decorators?" },
  hotel_banquet: { who: "Sneha Joshi", when: "Fri, 18 Dec", msg: "Hello, we need a banquet and 25 rooms for the baraat on 18 December. Could you share your package for 400 guests?" },
  catering: { who: "Anil Desai", when: "Thu, 3 Dec", msg: "Namaste, we need veg and non-veg catering for 450 guests on 3 December, with live counters. What is your per-plate rate?" },
  decoration: { who: "Kavya Hegde", when: "Sun, 22 Nov", msg: "Hi! Can you do mandap and stage décor in marigold and white on 22 November? Please share photos of your recent work." },
  photography: { who: "Neha Shetty", when: "13–14 Dec", msg: "Hello, we're looking for candid photos and a short film for a two-day wedding, 13 and 14 December. Are you available?" },
  anchor: { who: "Varun Naik", when: "Sat, 5 Dec", msg: "Hi, we need an anchor for our sangeet night on 5 December, in English and Kannada, with a few couple games. Are you free?" },
  musician: { who: "Ananya Rao", when: "Sat, 16 Jan", msg: "Namaste, we'd love a live band for the baraat and a DJ set after the reception on 16 January. What are your charges?" },
};

function OwnersSection() {
  const [cat, setCat] = useState("banquet_hall");
  const [price, setPrice] = useState(null);
  useEffect(() => { api.get("/config/pricing").then((r) => setPrice(withDefaults(r.data))).catch(() => setPrice(withDefaults({}))); }, []);
  const c = CATEGORIES.find((x) => x.key === cat) || CATEGORIES[0];
  const ask = SAMPLE_ASKS[cat];
  const six = Number((price || withDefaults({})).categories?.[cat]?.["6m"] || 0);
  const twelve = Number((price || withDefaults({})).categories?.[cat]?.["12m"] || 0);
  const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

  return (
    <section className="owners" data-testid="owner-cta-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-10 py-20 md:py-28">
        <div className="grid lg:grid-cols-[minmax(0,1fr)_minmax(0,1.05fr)] gap-12 lg:gap-16 items-start">
          <div>
            <h2 className="font-display text-[38px] sm:text-[48px] md:text-[58px] leading-[1.03] text-[color:var(--maroon)]">Get booked by families planning in your city.</h2>
            <p className="mt-5 text-[17px] leading-relaxed text-[color:var(--ink-2)] max-w-[52ch]">List once. Families find you, check your free dates and message you directly. You pay a fixed plan for your category and keep every rupee of the booking.</p>

            <p className="mt-9 text-[15px] font-semibold text-[color:var(--ink)]" id="owners-pick">What do you run?</p>
            <div className="mt-3 flex flex-wrap gap-2" role="radiogroup" aria-labelledby="owners-pick">
              {CATEGORIES.map((x) => (
                <button key={x.key} type="button" role="radio" aria-checked={cat === x.key} onClick={() => setCat(x.key)}
                  className={`owners-chip ${cat === x.key ? "is-on" : ""}`} data-testid={`owners-cat-${x.key}`}>{x.label}</button>
              ))}
            </div>

            <dl className="owners-terms mt-10" data-testid="owners-terms">
              <div><dt>Your plan</dt><dd><span className="font-num">{inr(six)}</span> for 6 months or <span className="font-num">{inr(twelve)}</span> a year</dd></div>
              <div><dt>Commission</dt><dd>None, on any booking</dd></div>
              <div><dt>Time to list</dt><dd>About 15 minutes, from your phone</dd></div>
            </dl>

            <div className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-4">
              <Link to={`/register?role=venue_owner&plan=${cat}`} className="btn-wine !px-7" data-testid="owner-cta-register">List my {c.label.toLowerCase()} business</Link>
              <Link to="/pricing" className="link-strong text-[15px]">Compare all plans</Link>
            </div>
          </div>

          {/* What arrives on your phone */}
          <div className="owners-stage" aria-live="polite">
            <div className="owners-phone">
              <div className="owners-phone-head">
                <span className="owners-avatar" aria-hidden>{ask.who.split(" ").map((w) => w[0]).join("")}</span>
                <div className="min-w-0">
                  <div className="text-[15px] font-semibold text-white leading-tight truncate">{ask.who}</div>
                  <div className="text-[12px] text-white/70">New inquiry from DartMyVenue</div>
                </div>
              </div>
              <div className="owners-chat">
                <div className="owners-day">Today</div>
                <AnimatePresence mode="wait">
                  <motion.div key={cat} initial={{ opacity: 0, y: 14, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.28, ease: [0.16, 1, 0.3, 1] }}>
                    <div className="owners-bubble" data-testid="owners-inquiry">
                      <p>{ask.msg}</p>
                      <span className="owners-time">10:42 AM</span>
                    </div>
                    <div className="owners-card">
                      <div className="flex items-center justify-between gap-3">
                        <span className="text-[13px] text-[color:var(--ink-2)]">Date asked</span>
                        <span className="text-[14px] font-semibold text-[color:var(--ink)]">{ask.when}</span>
                      </div>
                      <div className="mt-2 flex items-center justify-between gap-3">
                        <span className="text-[13px] text-[color:var(--ink-2)]">Your calendar</span>
                        <span className="text-[14px] font-semibold text-emerald-700">Free</span>
                      </div>
                    </div>
                    <div className="owners-replies" aria-label="Reply templates">
                      <span>Share packages</span><span>Invite for a visit</span><span>Send a quote</span>
                    </div>
                  </motion.div>
                </AnimatePresence>
              </div>
            </div>
            <p className="mt-4 text-[14px] text-[color:var(--ink-2)] text-center lg:text-left">Inquiries also wait for you in your business panel, with one-tap replies.</p>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------- Testimonials ---------- */
function Testimonials({ items }) {
  const { visitors } = useLive();
  const [i, setI] = useState(0);
  useEffect(() => { if (items.length < 2) return; const t = setInterval(() => setI((x) => (x + 1) % items.length), 6000); return () => clearInterval(t); }, [items.length]);
  if (!items.length) return null;
  const t = items[Math.min(i, items.length - 1)];
  const avg = items.reduce((n, x) => n + (Number(x.rating) || 5), 0) / items.length;
  return (
    <section className="jali py-20 md:py-28 border-y border-[color:var(--line)]" data-testid="testimonials-section">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 md:px-10 grid lg:grid-cols-[1fr_1.35fr] gap-12 items-center">
        <div>
          <Divider label="Reviews & testimonials"/>
          <h2 className="font-display text-4xl md:text-6xl mt-2 text-[color:var(--maroon)] leading-[1.02]">Loved by</h2>
          <div className="my-4" data-testid="testimonials-count"><FlipCounter value={visitors} digits={6} size="lg"/></div>
          <h2 className="font-display text-4xl md:text-6xl text-[color:var(--maroon)] leading-[1.02]">families</h2>
          <div className="mt-6 flex items-center gap-3">
            <div className="flex -space-x-3">{items.slice(0, 5).map((x, k) => x.avatar ? <img key={k} src={mediaUrl(x.avatar)} alt="" className="w-10 h-10 rounded-full object-cover ring-2 ring-[color:var(--cream-primary)] bg-[color:var(--ivory)]"/> : null)}</div>
            <div><div className="flex text-[color:var(--gold)]">{Array.from({ length: 5 }).map((_, k) => <Star key={k} className="w-4 h-4 fill-current"/>)}</div><div className="text-neutral-600 text-[14px]">{avg.toFixed(1)} average from {items.length} review{items.length === 1 ? "" : "s"}</div></div>
          </div>
          <div className="mt-8 flex gap-2">
            <button onClick={() => setI((i - 1 + items.length) % items.length)} className="w-11 h-11 rounded-full border-[1.5px] border-[color:var(--maroon)] text-[color:var(--maroon)] hover:bg-[color:var(--maroon)] hover:text-white transition-colors flex items-center justify-center press" aria-label="Previous" data-testid="testi-prev"><ChevronLeft className="w-5 h-5"/></button>
            <button onClick={() => setI((i + 1) % items.length)} className="w-11 h-11 rounded-full border-[1.5px] border-[color:var(--maroon)] text-[color:var(--maroon)] hover:bg-[color:var(--maroon)] hover:text-white transition-colors flex items-center justify-center press" aria-label="Next" data-testid="testi-next"><ChevronRight className="w-5 h-5"/></button>
          </div>
        </div>
        <div className="relative min-h-[300px]">
          <AnimatePresence mode="wait">
            <motion.figure key={i} initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -40 }} transition={{ duration: 0.45 }} className="card-heritage p-8 md:p-12">
              <Quote className="w-10 h-10 text-[color:var(--gold)]"/>
              <blockquote className="font-display text-2xl md:text-[34px] leading-snug text-[color:var(--maroon)] mt-4">{t.text}</blockquote>
              <figcaption className="mt-8 flex items-center gap-4">
                {t.avatar && <img src={mediaUrl(t.avatar)} alt={t.name} className="w-14 h-14 rounded-full object-cover ring-2 ring-[color:var(--gold-2)]"/>}
                <div>
                  <div className="font-semibold text-[color:var(--ink)]">{t.name}</div>
                  <div className="text-[14px] text-neutral-500">{t.city}</div>
                </div>
                <div className="ml-auto flex text-[color:var(--gold)]">{Array.from({ length: t.rating || 5 }).map((_, k) => <Star key={k} className="w-4 h-4 fill-current"/>)}</div>
              </figcaption>
            </motion.figure>
          </AnimatePresence>
          <div className="mt-4 flex gap-1.5 justify-center">{items.map((_, k) => <button key={k} onClick={() => setI(k)} className={`h-1.5 rounded-full transition-all ${k === i ? "w-8 bg-[color:var(--maroon)]" : "w-2 bg-[color:var(--gold)]/50"}`} aria-label={`Go to ${k + 1}`}/>)}</div>
        </div>
      </div>
    </section>
  );
}

/* ---------- Reels: autoplay chain via YouTube IFrame API ---------- */
const REELS = [
  {yt:"2Vv-BfVoq4g", title:"Shagun Lawns · Belgaum", sub:"Baraat to vidaai in 60 seconds"},
  {yt:"9zP1XmS0dg8", title:"Grand Palace · Bengaluru", sub:"A chandelier-lit reception"},
  {yt:"2Vv-BfVoq4g", title:"Royal Mandap · Jaipur", sub:"Palace wedding recap"},
];

function ReelSection() {
  const [idx, setIdx] = useState(0);
  const playerRef = useRef(null);
  const hostRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    const init = () => {
      if (cancelled || !hostRef.current || !window.YT?.Player) return;
      // YT replaces the target node with an iframe — give it a child we create ourselves so
      // React never loses track of a DOM node it owns.
      hostRef.current.innerHTML = "";
      const target = document.createElement("div");
      target.style.width = "100%"; target.style.height = "100%";
      hostRef.current.appendChild(target);
      playerRef.current = new window.YT.Player(target, {
        videoId: REELS[0].yt,
        playerVars: { autoplay: 1, mute: 1, controls: 1, rel: 0, playsinline: 1, modestbranding: 1 },
        events: {
          onReady: (e) => e.target.playVideo(),
          onStateChange: (e) => { if (e.data === window.YT.PlayerState.ENDED) setIdx((i) => (i + 1) % REELS.length); },
        },
      });
    };
    if (window.YT && window.YT.Player) init();
    else {
      if (!document.getElementById("yt-iframe-api")) {
        const s = document.createElement("script"); s.id = "yt-iframe-api"; s.async = true; s.src = "https://www.youtube.com/iframe_api"; document.body.appendChild(s);
      }
      const prev = window.onYouTubeIframeAPIReady;
      window.onYouTubeIframeAPIReady = () => { try { prev?.(); } catch {} init(); };
    }
    return () => { cancelled = true; try { playerRef.current?.destroy(); } catch {} playerRef.current = null; };
  }, []);

  useEffect(() => {
    const p = playerRef.current;
    if (p && typeof p.loadVideoById === "function") p.loadVideoById(REELS[idx].yt);
  }, [idx]);

  return (
    <section className="relative py-20 md:py-24 overflow-hidden bg-[color:var(--maroon-2)] text-[color:var(--cream-primary)]" data-testid="videos-section">
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 md:px-10">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
          <div>
            <div className="text-[color:var(--gold-2)] font-semibold">Wedding films</div>
            <h2 className="font-display text-4xl md:text-5xl mt-1">Films that made India smile</h2>
          </div>
          <div className="text-[14px] text-[#FBF7F4]/70">The next film starts when one ends</div>
        </div>
        <div className="grid lg:grid-cols-[1.6fr_1fr] gap-6">
          <div className="rounded-3xl overflow-hidden border border-[color:var(--gold)]/50 shadow-2xl bg-black aspect-video">
            <div ref={hostRef} className="w-full h-full" data-testid="reel-player"/>
          </div>
          <div className="flex lg:flex-col gap-3 overflow-x-auto no-scrollbar">
            {REELS.map((r, i) => (
              <button key={i} onClick={()=>setIdx(i)} className={`min-w-[240px] lg:min-w-0 text-left rounded-2xl p-4 border transition-colors flex gap-3 items-center ${i===idx ? "bg-[color:var(--gold)] text-[color:var(--maroon-2)] border-[color:var(--gold)]" : "border-[color:var(--gold)]/30 hover:bg-white/5"}`} data-testid={`reel-tab-${i}`}>
                <img src={`https://img.youtube.com/vi/${r.yt}/mqdefault.jpg`} alt="" className="w-20 h-14 rounded-lg object-cover"/>
                <div>
                  <div className="font-display text-lg leading-tight">{r.title}</div>
                  <div className={`text-xs ${i===idx ? "text-[color:var(--maroon)]/80" : "text-[color:var(--cream-primary)]/60"}`}>{i===idx ? "Now playing" : r.sub}</div>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------- FAQ ---------- */
const FAQS = [
  {q:"Who can list on DartMyVenue?", a:"Banquet halls, lawns, hotel banquets, caterers, decorators, photographers, anchors and musicians. Each business picks a 6-month or 12-month plan for its category — see the Pricing page for live rates."},
  {q:"Can I show my 2-year availability calendar?", a:"Yes. Every listing gets a 24-month calendar. Red = fully booked, amber = partial slots (great for caterers), green = available."},
  {q:"How does the 10-slot premium spotlight work?", a:"Each city has 10 premium slots that auto-slide on the homepage & listing page. Book up to 5 years ahead; combo & 12-month plans include a discount."},
  {q:"Do you take a commission on bookings?", a:"No. We're a listing platform, not a middleman. Owners pay only the subscription; every rupee from the couple goes to the venue."},
  {q:"Can I add my event manager or receptionist?", a:"Absolutely. Owners can invite staff to their panel. Super admins manage major admins & admins with fine-grained permissions."},
  {q:"When will renewal reminders be sent?", a:"Emails, in-app and WhatsApp-ready reminders go out 60, 45, 30, 20, 15, 10, 5 days before expiry — then daily till day 0, and follow-ups if not renewed."},
];
function FAQ() {
  const [open, setOpen] = useState(0);
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 md:px-10 py-20 md:py-24" data-testid="faq-section">
      <div className="grid lg:grid-cols-[1fr_1.5fr] gap-10">
        <div className="lg:sticky lg:top-28 self-start">
          <Divider label="Answers"/>
          <h2 className="font-display text-4xl md:text-6xl mt-3 text-[color:var(--maroon)] leading-[1]">Everything before you book</h2>
          <p className="text-neutral-600 mt-4 leading-relaxed">Still have a question? Our team replies within a few hours, every day.</p>
          <Link to="/contact" className="btn-ghost mt-6">Ask a question</Link>
        </div>
        <div className="divide-y divide-[color:var(--line)] border-y border-[color:var(--line)]">
          {FAQS.map((f, i)=>(
            <div key={i} data-testid={`faq-${i}`}>
              <button onClick={()=>setOpen(open===i ? -1 : i)} className="w-full text-left py-6 flex items-start gap-5 group" data-testid={`faq-toggle-${i}`}>
                                <span className="flex-1 font-display text-xl md:text-2xl text-[color:var(--maroon)] leading-snug">{f.q}</span>
                <span className="w-9 h-9 rounded-full border border-[color:var(--maroon)]/40 flex items-center justify-center text-[color:var(--maroon)] group-hover:bg-[color:var(--maroon)] group-hover:text-[color:var(--cream-primary)] transition-colors shrink-0">{open===i ? <Minus className="w-4 h-4"/> : <Plus className="w-4 h-4"/>}</span>
              </button>
              <AnimatePresence initial={false}>
                {open===i && (
                  <motion.div initial={{height:0, opacity:0}} animate={{height:"auto", opacity:1}} exit={{height:0, opacity:0}} transition={{duration:0.3}} className="overflow-hidden">
                    <p className="pb-6 pr-12 text-[color:var(--ink-2)] leading-relaxed text-[16px]">{f.a}</p>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function Newsletter({ compact = false }) {
  const [email, setEmail] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null); // { email, already }
  const submit = async (e) => {
    e.preventDefault();
    const bad = emailError(email, { required: true });
    setErr(bad);
    if (bad) return;
    setBusy(true);
    try {
      const { data } = await api.post("/newsletter/subscribe", { email: email.trim().toLowerCase(), source: "home" });
      setDone({ email: email.trim().toLowerCase(), already: !!data?.already });
      toast.success(data?.already ? "You're already subscribed" : "Subscribed");
      setEmail("");
    } catch (er) {
      const st = er?.response?.status;
      setErr(st === 404 || st === 405 ? "Subscriptions are being updated on our server. Please try again in a little while." : formatApiErrorDetail(er?.response?.data?.detail) || "Couldn't subscribe. Check your connection and try again.");
    } finally { setBusy(false); }
  };
  return (
    <section className={compact ? "" : "pb-20 px-4 sm:px-6 md:px-10"} data-testid="newsletter-section">
      <div className={`${compact ? "" : "max-w-5xl mx-auto"} rounded-[28px] jali-dark text-[color:var(--cream-primary)] p-8 md:p-14 text-center border border-[color:var(--gold)]/50`}>
        <h2 className="font-display text-3xl md:text-5xl mt-2">Wedding wisdom, in your inbox</h2>
        <p className="mt-3 text-[color:var(--cream-primary)]/75 max-w-xl mx-auto text-sm md:text-base">One email a month: new venues in your city, seasonal checklists and budget tips. Unsubscribe any time.</p>
        {done ? (
          <p className="mt-8 text-[17px] text-[color:var(--cream-primary)]" role="status" data-testid="newsletter-done">
            {done.already ? <>You're already on the list as <b>{done.email}</b>.</> : <>Done. The next letter goes to <b>{done.email}</b>.</>}{" "}
            <button onClick={() => setDone(null)} className="underline underline-offset-4 text-[color:var(--gold-2)]">Use another email</button>
          </p>
        ) : (
          <form onSubmit={submit} noValidate className="mt-8 max-w-md mx-auto">
            <div className="flex flex-col sm:flex-row gap-2">
              <input type="email" inputMode="email" autoComplete="email" value={email} onChange={(e)=>{ setEmail(e.target.value); if (err) setErr(""); }} placeholder="you@example.com" aria-invalid={!!err} aria-label="Email address" className={`flex-1 px-5 py-3.5 rounded-full border bg-white text-[15px] text-[color:var(--ink)] outline-none focus:ring-2 focus:ring-[color:var(--gold-2)] ${err ? "border-rose-400" : "border-[color:var(--gold)]/60"}`} data-testid="newsletter-email"/>
              <button disabled={busy} className="btn-gold" data-testid="newsletter-submit">{busy ? "Subscribing…" : "Subscribe"}</button>
            </div>
            {err && <p className="mt-3 text-[14px] text-[#FFC9C9]" role="alert" data-testid="newsletter-error">{err}</p>}
          </form>
        )}
      </div>
    </section>
  );
}
